import { useEffect, useRef, useState } from "react";

import { getChatHistory, getMe, getMyGroups, type Me } from "../api";
import { Chat } from "../chat/Chat";

/**
 * Onboarding trap. A logged-in user with an incomplete profile (no name / sport
 * / skill) is held here until the bot has collected the essentials — there are
 * no tabs to escape to. It's just the real chat onboarding full-screen, plus a
 * header that flips to a "Continue" button the moment the profile is complete.
 */
export function Onboarding({
  me,
  onDone,
  onSignOut,
}: {
  me: Me;
  onDone: (me: Me) => void;
  onSignOut: () => void;
}) {
  const [seed, setSeed] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [progress, setProgress] = useState(me.onboarding ?? { completed: 0, total: 3 });
  // Name the club an invite user just joined, so the header makes it clear
  // they're in (and which one). Standard signups have no group yet → null.
  const [clubName, setClubName] = useState<string | null>(null);
  // Once true we've handed off to the home page; guards against the auto-advance
  // timer and a manual Continue click both firing onDone.
  const navigated = useRef(false);

  const pct = done ? 100 : Math.round((progress.completed / progress.total) * 100);

  // Kick the bot off only when there's no prior conversation — a club join
  // already injects a welcome that asks for the name, so we don't double up.
  useEffect(() => {
    let cancelled = false;
    getChatHistory()
      .then((h) => {
        if (!cancelled && h.length === 0) {
          setSeed("Hi! I'm new here — help me set up my profile.");
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch any club they belong to (an invite auto-joined them just before this).
  useEffect(() => {
    let cancelled = false;
    getMyGroups()
      .then((r) => {
        if (!cancelled && r.groups.length > 0) setClubName(r.groups[0]!.name);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  function finish(fresh: Me | null) {
    if (navigated.current) return;
    navigated.current = true;
    onDone(fresh ?? { ...me, onboarded: true });
  }

  // After each bot turn the profile may have just become complete. When it is,
  // let the bot's closing message land for a beat, then take them home — the
  // top-bar Continue button was easy to miss.
  async function recheck() {
    const fresh = await getMe().catch(() => null);
    if (fresh?.onboarding) setProgress(fresh.onboarding);
    if (fresh?.onboarded && !done) {
      setDone(true);
      setTimeout(() => finish(fresh), 1500);
    }
  }

  async function handleContinue() {
    const fresh = await getMe().catch(() => null);
    finish(fresh);
  }

  return (
    <div className="h-[100dvh]">
      <header className="relative h-16 border-b border-ink/10 bg-bg flex items-center justify-between gap-3 px-4 sm:px-5">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-sm bg-ink flex items-center justify-center shrink-0">
            <div className="w-4 h-4 rounded-full bg-accent" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-base leading-none truncate">
              {clubName ? `Joining ${clubName}` : "Set up your profile"}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink/45 mt-1 truncate">
              {done
                ? "All set — taking you in…"
                : clubName
                  ? "You're in — a few quick questions"
                  : "A few quick questions"}
            </div>
          </div>
        </div>
        {done ? (
          <button
            onClick={() => void handleContinue()}
            className="cs-tap shrink-0 px-4 py-2 bg-accent text-ink rounded-lg font-display font-bold text-sm hover:brightness-105"
          >
            Continue →
          </button>
        ) : (
          <button
            onClick={onSignOut}
            className="cs-tap shrink-0 font-mono text-[10px] uppercase tracking-wider text-ink/45 hover:text-ink"
          >
            Sign out
          </button>
        )}
        {/* Thin progress bar riding the header's bottom border. Tracks the
            visible steps (name/sport/skill) and snaps to full on completion. */}
        <div
          className="absolute bottom-0 left-0 h-1 bg-accent transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </header>

      <Chat
        me={me}
        onSignOut={onSignOut}
        pendingSeed={seed}
        onSeedConsumed={() => setSeed(null)}
        onTurnComplete={() => void recheck()}
      />
    </div>
  );
}
